import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { t } from "@/lib/i18n";
import {
  MessageCircle, Sunrise, Brain, Wand2, BellRing, Paperclip,
  CalendarCheck, ArrowRight, Check, Mic, Mail, Sparkles,
  Inbox, Send, Star, Bot,
} from "lucide-react";
import api from "@/lib/api";
import { useSite, maybeRedirectToLang, renderHl } from "@/lib/site";
import "./demo.css";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
};

const Label = ({ children, light }) => (
  <p className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${light ? "text-white/50" : "text-[#86868B]"}`}>{children}</p>
);

/* ---------- WhatsApp phone mock ---------- */
const WA_SCRIPT = [
  { me: true, text: "Anything important this morning?" },
  { me: false, text: "3 things: Laura (VIP) asked to move the demo to Thursday, the Deloitte contract PDF arrived, and 2 invoices need approval. The rest was noise — archived." },
  { me: true, text: "Reply to Laura that Thursday 10am works. My tone." },
  { me: false, text: "Draft ready: \"Hi Laura, Thursday 10am works perfectly on my side...\" — Send it?" },
  { me: true, text: "Send ✓" },
];

const PhoneMock = ({ brandName = "Agentic Inbox", chats = WA_SCRIPT }) => {
  const [step, setStep] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let i = 0;
        const iv = setInterval(() => { i += 1; setStep(i); if (i >= chats.length) clearInterval(iv); }, 950);
        obs.disconnect();
      }
    }, { threshold: 0.35 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} data-testid="landing-phone-mock"
      className="relative w-[320px] sm:w-[360px] rounded-[2.8rem] border border-black/10 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.12)] overflow-hidden">
      <div className="bg-[#075E54] text-white px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Sparkles size={16} /></div>
        <div>
          <p className="text-sm font-semibold leading-none">{brandName}</p>
          <p className="text-[11px] text-white/70 mt-1">online · WhatsApp</p>
        </div>
      </div>
      <div className="bg-[#ECE5DD] px-4 py-5 space-y-2.5 min-h-[380px]">
        {chats.slice(0, step).map((m, i) => (
          <div key={i} className={`demo-bubble flex ${m.me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-snug shadow-sm ${m.me ? "bg-[#DCF8C6] text-[#1d1d1f] rounded-br-md" : "bg-white text-[#1d1d1f] rounded-bl-md"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {step < chats.length && (
          <div className="demo-bubble flex justify-start" style={{ animationDelay: "0.2s" }}>
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm demo-typing text-[#86868B] text-sm font-bold">
              <span>·</span><span>·</span><span>·</span>
            </div>
          </div>
        )}
      </div>
      <div className="bg-[#F7F7F7] border-t border-black/5 px-4 py-3 flex items-center gap-2">
        <div className="flex-1 bg-white rounded-full border border-black/10 px-4 py-2 text-[13px] text-[#86868B]">Message</div>
        <div className="w-9 h-9 rounded-full bg-[#075E54] flex items-center justify-center text-white"><Mic size={15} /></div>
      </div>
    </div>
  );
};

/* ---------- Web app product mockup (kept from previous homepage) ---------- */
const AppMock = () => (
  <div className="border border-black/10 rounded-3xl shadow-[0_24px_80px_rgba(0,0,0,0.1)] overflow-hidden select-none bg-white" data-testid="hero-mockup">
    <div className="h-9 bg-[#F5F5F7] border-b border-black/5 flex items-center px-4 gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" />
    </div>
    <div className="grid grid-cols-12 h-[380px] text-left">
      <div className="col-span-2 bg-[#FAFAFC] border-r border-black/5 p-3 hidden md:block">
        <div className="bg-[#0071E3] text-white text-xs font-semibold rounded-full py-1.5 text-center mb-3">Compose</div>
        {[["Inbox", Inbox, "52"], ["Sent", Send, ""], ["Starred", Star, ""]].map(([l, I, b]) => (
          <div key={l} className="flex items-center gap-2 text-xs text-[#1d1d1f]/60 py-1.5 px-1"><I size={13} />{l}<span className="ml-auto text-[10px] text-[#86868B]">{b}</span></div>
        ))}
      </div>
      <div className="col-span-4 border-r border-black/5 hidden md:block">
        {[["Elena Vasquez", "Intro call next week — Series A", true], ["Nadia Rahman", "Q3 Roadmap sync — action items", true], ["John Murphy", "Requesting beta access", false], ["Priya Sharma", "Candidate shortlist ready", false]].map(([n, s, u]) => (
          <div key={n} className="px-3 py-2.5 border-b border-black/5 flex gap-2">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${u ? "bg-[#0071E3]" : "bg-transparent"}`} />
            <div><div className={`text-xs ${u ? "font-semibold text-[#1d1d1f]" : "text-[#1d1d1f]/60"}`}>{n}</div><div className="text-[11px] text-[#86868B] truncate w-44">{s}</div></div>
          </div>
        ))}
      </div>
      <div className="col-span-12 md:col-span-3 border-r border-black/5 p-4">
        <div className="text-sm font-bold text-[#1d1d1f] demo-heading">Intro call next week</div>
        <div className="text-[11px] text-[#86868B] mb-3">2 messages in this thread</div>
        <div className="border-l-2 border-amber-400 bg-amber-50/50 rounded p-2.5">
          <div className="text-[10px] font-semibold text-[#86868B] mb-1">DRAFT REPLY</div>
          <div className="text-[11px] text-[#1d1d1f]/80 leading-relaxed">Hi Elena, Tuesday 2pm works great — looking forward to discussing the round…</div>
          <div className="flex gap-1.5 mt-2">
            <span className="bg-[#0071E3] text-white text-[10px] font-semibold rounded-full px-2.5 py-0.5">Send</span>
            <span className="border border-black/10 text-[#1d1d1f]/60 text-[10px] rounded-full px-2.5 py-0.5">Edit</span>
          </div>
        </div>
      </div>
      <div className="col-span-3 bg-[#FAFAFC] p-3 hidden md:flex flex-col">
        <div className="text-[11px] font-semibold text-[#1d1d1f]/70 mb-2 flex items-center gap-1"><Bot size={12} /> Email Agent</div>
        <div className="bg-[#0071E3] text-white text-[11px] rounded-lg rounded-br-sm px-2.5 py-1.5 self-end mb-2">Draft a warm reply and book Tuesday 2pm</div>
        <div className="bg-white border border-black/5 text-[#1d1d1f]/80 text-[11px] rounded-lg px-2.5 py-1.5 mb-1.5">Created draft reply to elena@meridianventures.com</div>
        <div className="bg-white border border-black/5 text-[#1d1d1f]/80 text-[11px] rounded-lg px-2.5 py-1.5">Scheduled "Series A intro call" — Tue 2:00 PM</div>
        <div className="mt-auto border border-black/10 rounded-full px-3 py-1.5 text-[11px] text-[#86868B] bg-white">Ask your email agent…</div>
      </div>
    </div>
  </div>
);

/* ---------- Bento cells ---------- */
const BENTO = [
  { span: "md:col-span-4", icon: Sunrise, tag: "Morning Brief", title: "One message. Whole morning sorted.",
    body: "Every morning on WhatsApp: VIP mail first, who still owes you a reply, follow-ups due, today's meetings — before your first coffee." },
  { span: "md:col-span-4", icon: Brain, tag: "Agent Memory", title: "It remembers so you don't have to.",
    body: "\"Laura prefers Thursdays.\" \"Acme's invoice terms are 45 days.\" Your agent learns durable facts about every contact and writes with them." },
  { span: "md:col-span-4", icon: Wand2, tag: "Automations", title: "Rules in plain language.",
    body: "\"Archive every newsletter.\" \"Alert me on WhatsApp when a VIP writes.\" Say it once — it runs forever." },
  { span: "md:col-span-6", icon: BellRing, tag: "Follow-ups", title: "Nothing slips again.",
    body: "\"Follow up if she doesn't reply by Friday.\" The agent watches the thread and nudges — you stop keeping mental lists." },
  { span: "md:col-span-6", icon: Paperclip, tag: "Documents", title: "It reads the attachments too.",
    body: "Contracts, invoices, PDFs — ask \"what's the penalty clause in Deloitte's contract?\" and get the answer, not the homework." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [audTab, setAudTab] = useState("personal");
  const audienceOf = (p) => p.audience || (p.seats <= 1 ? "personal" : "team");
  const shownPackages = packages.filter((p) => audienceOf(p) === audTab);
  const [buying, setBuying] = useState("");
  const { g, s, brand, seo } = useSite("landing");
  const { g: gp } = useSite("pricing");
  const bn = brand.app_name || "Agentic Inbox";
  const chats = WA_SCRIPT.map((m, i) => ({ ...m, text: g(`chat_${i + 1}`, m.text) }));

  useEffect(() => { maybeRedirectToLang(); }, []);
  useEffect(() => {
    document.title = seo?.meta_title || "Agentic Inbox — Your inbox, answered.";
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, [seo]);

  const choosePackage = (pkg) => {
    const token = localStorage.getItem("agi_token");
    if (token) { navigate("/billing"); return; }
    localStorage.setItem("agi_pref_plan", pkg.id);
    navigate(`/login?package=${pkg.id}`);
  };

  const gridCols = shownPackages.length >= 3 ? "md:grid-cols-3" : shownPackages.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-1 max-w-md mx-auto";

  return (
    <div className="demo-root min-h-screen" data-testid="landing-page">
      {/* NAV — Sign in + Get Started grouped right */}
      <header className="demo-nav fixed top-0 inset-x-0 z-50 h-16 max-md:h-auto">
        <div className="max-w-6xl mx-auto h-16 px-6 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="landing-brand">
            <div className="w-7 h-7 rounded-lg bg-[#0071E3] flex items-center justify-center text-white overflow-hidden">
              {brand.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={bn} className="w-7 h-7 object-cover" data-testid="landing-brand-logo" /> : <Mail size={14} />}
            </div>
            <span className="demo-heading font-semibold text-[15px] tracking-tight">{bn}</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-[13px] text-[#1d1d1f]/70">
            <Link to="/features" data-testid="nav-features-link" className="hover:text-[#1d1d1f] transition-colors">{s("nav_features", "Features")}</Link>
            <Link to="/pricing" data-testid="nav-pricing-link" className="hover:text-[#1d1d1f] transition-colors">{s("nav_pricing", "Pricing")}</Link>
          </nav>
          <div className="flex items-center gap-4">
            <button data-testid="nav-signin-btn" onClick={() => navigate("/login")}
              className="text-[13px] font-medium text-[#1d1d1f]/70 hover:text-[#1d1d1f] transition-colors">{t("landing.sign_in", "Sign in")}</button>
            <a href="#pricing" data-testid="nav-getstarted-btn"
              className="demo-pill bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold px-5 py-2">{s("nav_getstarted", t("landing.get_started", "Get Started"))}</a>
          </div>
        </div>
        <nav className="md:hidden flex items-center justify-center gap-8 text-[13px] text-[#1d1d1f]/70 pb-2.5 -mt-1" data-testid="nav-mobile">
          <Link to="/features" data-testid="nav-features-link-mobile" className="hover:text-[#1d1d1f] transition-colors">{s("nav_features", "Features")}</Link>
          <Link to="/pricing" data-testid="nav-pricing-link-mobile" className="hover:text-[#1d1d1f] transition-colors">{s("nav_pricing", "Pricing")}</Link>
        </nav>
      </header>

      {/* HERO */}
      <section className="relative demo-grain pt-40 pb-16 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <Label>{g("hero_label", "The email agent that lives in your WhatsApp")}</Label>
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tighter leading-[1.05] text-balance">
              {g("hero_title", "Your inbox answered itself while you had breakfast.")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#86868B] leading-relaxed max-w-2xl mx-auto">
              {g("hero_sub", "Read, triage, draft and send email — from a WhatsApp chat, by text or voice note. An AI agent that knows your contacts, your tone and your deadlines.")}
            </p>
            <div className="mt-9 flex items-center justify-center gap-3">
              <a href="#pricing" data-testid="hero-cta-btn"
                className="demo-pill bg-[#0071E3] hover:bg-[#0077ED] text-white text-[15px] font-semibold px-8 py-3.5">
                {t("landing.get_started", "Get Started")}
              </a>
              <a href="#features" data-testid="hero-secondary-btn"
                className="demo-pill bg-white border border-black/10 text-[#1d1d1f] text-[15px] font-semibold px-8 py-3.5 hover:bg-black/[0.03] inline-flex items-center gap-2">
                {g("cta_see_how", "See how")} <ArrowRight size={15} />
              </a>
            </div>
          </motion.div>
        </div>
        <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.15 }} className="mt-16 flex justify-center">
          <PhoneMock brandName={bn} chats={chats} />
        </motion.div>
      </section>

      {/* LOSS / GAIN STATEMENT */}
      <section className="py-28 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <Label>{g("loss_label", "What it costs you today")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] text-balance">
              {renderHl(g("loss_title", "You spend **3 hours a day** inside email. The people you compete with spend 20 minutes — ~~their agent handles the rest.~~"))}
            </h2>
            <p className="mt-6 text-lg text-[#86868B] leading-relaxed max-w-2xl">
              {g("loss_sub", "Not because they work less. Because triage, drafting, chasing replies and reading attachments were never work worth doing by hand.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHATSAPP HIGHLIGHT */}
      <section id="features" className="py-24 px-6 bg-white border-y border-black/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <Label>{g("wa_label", "The part your laptop can't do")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] text-balance">
              {g("wa_title", "Your whole inbox, inside WhatsApp.")}
            </h2>
            <p className="mt-6 text-lg text-[#86868B] leading-relaxed">
              {g("wa_sub", "No app to open, no tab to find. The one chat window you already check 60 times a day becomes your email command line.")}
            </p>
            <ul className="mt-8 space-y-4">
              {[
                g("wa_point_1", "Ask what matters — get a triaged answer, not 40 unread"),
                g("wa_point_2", "Dictate a reply as a voice note — it writes it in your style"),
                g("wa_point_3", "VIP writes you → instant WhatsApp ping"),
                g("wa_point_4", "Morning Brief delivered before your day starts"),
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0071E3]/10 text-[#0071E3] flex items-center justify-center shrink-0"><Check size={12} strokeWidth={3} /></span>
                  <span className="text-[#1d1d1f]/85">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="demo-card p-10 bg-[#FAFAFC]">
            <MessageCircle size={28} className="text-[#0071E3]" strokeWidth={1.8} />
            <p className="demo-heading mt-6 text-2xl font-medium tracking-tight leading-snug">
              {g("wa_quote", "\"Summarize my unread, reply to the urgent one, remind me about the rest tomorrow.\"")}
            </p>
            <p className="mt-4 text-[#86868B] text-[15px]">{g("wa_quote_sub", "One voice note. Done. That's the entire product.")}</p>
            <div className="mt-8 pt-6 border-t border-black/5 grid grid-cols-3 gap-4 text-center">
              {[[g("stat_1_n", "2.6h"), g("stat_1_l", "saved daily")], [g("stat_2_n", "<60s"), g("stat_2_l", "to inbox zero brief")], [g("stat_3_n", "24/7"), g("stat_3_l", "watching your mail")]].map(([n, l]) => (
                <div key={l}>
                  <p className="demo-heading text-2xl font-semibold tracking-tight text-[#0071E3]">{n}</p>
                  <p className="text-[11px] uppercase tracking-widest text-[#86868B] mt-1">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* WEB APP — kept product mockup from previous homepage */}
      <section className="py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Label>{g("app_label", "And when you're at your desk")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-balance">{g("app_title", "A full command center, with the agent at your side.")}</h2>
            <p className="mt-5 text-lg text-[#86868B] leading-relaxed">
              {g("app_sub", "The web app pairs your inbox with the agent panel — it drafts, files, schedules and executes while you read. WhatsApp on the move, this at your desk.")}
            </p>
          </motion.div>
          <motion.div {...fadeUp}>
            <AppMock />
          </motion.div>
        </div>
      </section>

      {/* BENTO GRID */}
      <section className="pb-28 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="mb-14 max-w-2xl">
            <Label>{g("bento_label", "Everything else it quietly does")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-balance">{g("bento_title", "An agent, not another inbox tab.")}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {BENTO.map((b, i) => (
              <motion.div key={b.tag} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`demo-card p-8 flex flex-col ${b.span}`} data-testid={`feature-card-${i}`}>
                <b.icon size={26} className="text-[#0071E3]" strokeWidth={1.8} />
                <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mt-6">{g(`bento_${i + 1}_tag`, b.tag)}</p>
                <h3 className="demo-heading text-xl md:text-2xl font-medium tracking-tight mt-2">{g(`bento_${i + 1}_title`, b.title)}</h3>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-3">{g(`bento_${i + 1}_body`, b.body)}</p>
              </motion.div>
            ))}
            <motion.div {...fadeUp} className="demo-card demo-dark p-8 flex flex-col md:col-span-12 md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <CalendarCheck size={26} className="text-[#2997FF]" strokeWidth={1.8} />
                <h3 className="demo-heading text-xl md:text-2xl font-medium tracking-tight mt-4 text-[#F5F5F7]">
                  {g("cal_title", "\"Schedule a meeting with Elena tomorrow at 2pm.\"")}
                </h3>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-3 max-w-xl">
                  {g("cal_body", "Straight into your Google Calendar — invite sent, thread linked, reminder set. Email, calendar and follow-ups finally act like one system.")}
                </p>
              </div>
              <a href="#pricing" data-testid="bento-cta"
                className="demo-pill bg-white text-[#1d1d1f] text-[14px] font-semibold px-7 py-3 self-start md:self-center shrink-0 hover:bg-[#F5F5F7]">
                {g("cal_cta", "Try it on your inbox")}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GAIN / LIFESTYLE */}
      <section className="py-24 px-6 bg-white border-y border-black/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp} className="order-2 md:order-1">
            <img src="https://images.pexels.com/photos/7683664/pexels-photo-7683664.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Time back — coffee away from the desk" loading="lazy"
              className="rounded-3xl border border-black/5 shadow-[0_16px_48px_rgba(0,0,0,0.08)] w-full object-cover aspect-[4/3]" />
          </motion.div>
          <motion.div {...fadeUp} className="order-1 md:order-2">
            <Label>{g("gain_label", "What you get back")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] text-balance">
              {g("gain_title", "The inbox keeps working. You get to stop.")}
            </h2>
            <p className="mt-6 text-lg text-[#86868B] leading-relaxed">
              {g("gain_sub", "Solopreneurs get their evenings back. Teams get one agent per seat — memory, rules and briefs for every member, managed like Google Workspace.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* PRICING — live plans */}
      <section id="pricing" className="py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-10">
            <Label>{g("pricing_label", "Plans")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-balance">{g("pricing_title", "Priced like a tool. Pays back like a hire.")}</h2>
          </motion.div>
          <motion.div {...fadeUp} className="flex justify-center mb-12">
            <div className="inline-flex rounded-full border border-black/10 bg-white p-1" data-testid="pricing-tabs">
              <button data-testid="pricing-tab-personal" onClick={() => setAudTab("personal")}
                className={`demo-pill px-6 py-2 text-[13px] font-semibold ${audTab === "personal" ? "bg-[#0071E3] text-white" : "text-[#1d1d1f]/60 hover:text-[#1d1d1f]"}`}>{gp("tab_me", "For me")}</button>
              <button data-testid="pricing-tab-team" onClick={() => setAudTab("team")}
                className={`demo-pill px-6 py-2 text-[13px] font-semibold ${audTab === "team" ? "bg-[#0071E3] text-white" : "text-[#1d1d1f]/60 hover:text-[#1d1d1f]"}`}>{gp("tab_team", "For my team")}</button>
            </div>
          </motion.div>
          <div className={`grid grid-cols-1 gap-6 items-stretch ${gridCols}`} data-testid="pricing-grid">
            {shownPackages.map((pkg, i) => {
              const slug = pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <motion.div key={pkg.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.07 }}
                  data-testid={`pricing-card-${slug}`}
                  className={`demo-card relative p-9 flex flex-col ${pkg.popular ? "ring-2 ring-[#0071E3]" : ""}`}>
                  {pkg.popular && (
                    <span className="absolute -top-3 left-8 bg-[#0071E3] text-white text-[11px] font-bold rounded-full px-3 py-1">{gp("badge_popular", "Most popular")}</span>
                  )}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B]">{pkg.name}</p>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span data-testid={`pricing-price-${slug}`} className="demo-heading text-4xl font-semibold tracking-tighter text-[#1d1d1f]">
                      ${pkg.price_monthly % 1 === 0 ? pkg.price_monthly.toFixed(0) : pkg.price_monthly}
                    </span>
                    <span className="text-[13px] text-[#86868B]">{gp("per_month", "/month")} · {pkg.seats} {pkg.seats > 1 ? gp("seat_many", "seats") : gp("seat_one", "seat")}</span>
                  </div>
                  <ul className="space-y-3 flex-1 mt-7 mb-8">
                    {(pkg.features || []).map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-[15px] text-[#1d1d1f]/85">
                        <Check size={15} strokeWidth={2.5} className="text-[#0071E3] mt-0.5 shrink-0" /> {feat}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`pricing-cta-${slug}`} onClick={() => choosePackage(pkg)} disabled={buying === pkg.id}
                    className={`demo-pill w-full py-3 text-[14px] font-semibold disabled:opacity-50 ${pkg.popular ? "bg-[#0071E3] hover:bg-[#0077ED] text-white" : "bg-white border border-black/10 text-[#1d1d1f] hover:bg-black/[0.03]"}`}>
                    {buying === pkg.id ? "Redirecting…" : pkg.price_monthly <= 0 ? gp("cta_start_free", "Start free") : `${gp("cta_get", "Get")} ${pkg.name}`}
                  </button>
                </motion.div>
              );
            })}
            {packages.length === 0 && <p className="text-sm text-[#86868B] text-center md:col-span-3">No packages published yet.</p>}
            {packages.length > 0 && shownPackages.length === 0 && (
              <p data-testid="pricing-empty-tab" className="text-sm text-[#86868B] text-center md:col-span-3">
                No {audTab === "personal" ? "personal" : "team"} plans published yet —{" "}
                <button onClick={() => setAudTab(audTab === "personal" ? "team" : "personal")} className="text-[#0071E3] underline">
                  check out the {audTab === "personal" ? "team" : "personal"} plans →</button>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-28 px-6 bg-[#1d1d1f] demo-grain relative">
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#F5F5F7] text-balance">
              {g("cta_title", "Tomorrow morning, let it answer for you.")}
            </h2>
            <p className="mt-5 text-lg text-[#86868B]">{g("cta_sub", "Connect Gmail, link WhatsApp, done in 3 minutes.")}</p>
            <a href="#pricing" data-testid="footer-cta"
              className="demo-pill inline-block mt-9 bg-white text-[#1d1d1f] text-[15px] font-semibold px-9 py-4 hover:bg-[#F5F5F7]">
              {t("landing.get_started", "Get Started")}
            </a>
          </motion.div>
        </div>
      </section>

      {/* PROPER FOOTER */}
      <footer className="bg-[#1d1d1f] border-t border-white/10 pt-16 pb-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-12">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-[#0071E3] flex items-center justify-center text-white overflow-hidden">
                  {brand.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={bn} className="w-7 h-7 object-cover" data-testid="landing-footer-logo" /> : <Mail size={14} />}
                </div>
                <span className="demo-heading font-semibold text-[15px] tracking-tight text-[#F5F5F7]">{bn}</span>
              </div>
              <p className="text-[13px] text-[#86868B] leading-relaxed max-w-xs">
                {s("footer_tagline", "The AI email agent for business owners and solopreneurs — in your inbox, your calendar and your WhatsApp.")}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_product", "Product")}</p>
              <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
                <li><Link to="/features" className="hover:text-white transition-colors">{s("nav_features", "Features")}</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">{s("nav_pricing", "Pricing")}</Link></li>
                <li><button data-testid="footer-signin" onClick={() => navigate("/login")} className="hover:text-white transition-colors">{s("nav_signin", t("landing.sign_in", "Sign in"))}</button></li>
                <li><Link data-testid="footer-distributor" to="/become-a-distributor" className="hover:text-white transition-colors">{s("footer_distributor", "Become a distributor")}</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_languages", "Languages")}</p>
              <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
                <li><a href="/?lang=en" className="hover:text-white transition-colors">English</a></li>
                <li><a href="/es/" className="hover:text-white transition-colors">Español</a></li>
                <li><a href="/pt/" className="hover:text-white transition-colors">Português</a></li>
                <li><a href="/fr/" className="hover:text-white transition-colors">Français</a></li>
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_legal", "Legal")}</p>
              <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
                <li><a data-testid="mkt-footer-privacy" href="/privacy-policy" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{s("footer_privacy", "Privacy Policy")}</a></li>
                <li><a data-testid="mkt-footer-terms" href="/terms" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{s("footer_terms", "Terms & Conditions")}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#86868B]">
            <p>{s("footer_copyright", "© 2026 Agentic Inbox. All rights reserved.")}</p>
            <p>{s("footer_meta", "Gmail sync · WhatsApp agent · Team seats")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
